<template>
  <v-card>

    <v-data-table :headers="headers"
                  :items="courses"
                  :options.sync="options"
                  :footer-props="dataTableFooterProps"
                  :server-items-length="totalCourses"
                  :loading="coursesLoading"
                  disable-sort
                  loading-text="Loading... Please wait">

      <template v-slot:top>
        <v-card-title class="grey--text text--darken-2">
          <v-icon class="mx-3">
            mdi-book-plus
          </v-icon>
          Virtual Learning Environment CMS: Courses
        </v-card-title>

        <v-card-title class="mb-10">
          <v-btn class="mr-5 ml-2"
                 rounded
                 color="primary"
                 dark
                 large
                 @click="addItem()"
          >
            <v-icon
            >
              mdi-plus
            </v-icon>
            Add a course
          </v-btn>

          <v-text-field
              v-model="search"
              append-icon="mdi-magnify"
              label="Search Courses"
              filled
              rounded
              single-line
              hide-details
              placeholder="Search courses"
              class="mr-5"
              clearable
              @click="clear"
              color="primary"
          ></v-text-field>

          <RefreshTableHeader :action="refresh" />
        </v-card-title>
      </template>

      <template v-slot:item.priceInUnits="{ item }">
        {{ (item.priceInUnits / 100).toFixed(2) }} {{ item.currency }}
      </template>

      <template v-slot:item.popular="{ item }">
        <v-icon v-if="item.popular">mdi-checkbox-outline</v-icon>
        <v-icon v-else>mdi-checkbox-blank-outline</v-icon>
      </template>

      <template v-slot:item.level="{ item }">
        {{ item.level ? item.level.name : '' }}
      </template>

      <template v-slot:item.certificationBody="{ item }">
        {{ item.certificationBody ? item.certificationBody.name : '' }}
      </template>

      <template v-slot:item.categories="{ item }">
        <v-chip
            v-for="category in item.categories"
            :key="category._id"
            small
            class="ma-1"
        >
          {{ category.name }}
        </v-chip>
      </template>

      <template v-slot:item.tags="{ item }">
        <TagsDisplay :items="item.tags"/>
      </template>

      <template v-slot:item.resources="{ item }">
        <v-chip>{{ item.resources ? item.resources.length : 0 }}</v-chip>
      </template>

      <template v-slot:item.actions="{ item }">

        <v-btn x-small
               depressed
               class="pa-3 ma-1"
               @click="editItem(item)">
          <v-icon small>
            mdi-pencil
          </v-icon>
        </v-btn>

        <v-btn
            x-small
            depressed
            @click="deleteItem(item)"
            class="pa-3 ma-1">
          <v-icon small color="primary">
            mdi-close
          </v-icon>
        </v-btn>
      </template>

      <template v-slot:[`footer.page-text`]="items">
        <RefreshTableFooter :item="items" :action="refresh"/>
      </template>

    </v-data-table>

    <CourseAdd/>
    <CourseEdit/>
    <CourseDelete/>

  </v-card>
</template>

<script>

import { mapActions, mapGetters, mapState } from "vuex";
import CourseEdit from "@/components/Courses/CourseEdit";
import CourseDelete from "@/components/Courses/CourseDelete";
import CourseAdd from "@/components/Courses/CourseAdd";
import RefreshTableHeader from "@/components/common/RefreshTableHeader";
import RefreshTableFooter from "@/components/common/RefreshTableFooter";
import TagsDisplay from "@/components/common/TagsDisplay";
import {debounce} from "@/utility";

export default {
  name: "Courses",
  components: {RefreshTableHeader, RefreshTableFooter, CourseAdd, CourseDelete, CourseEdit, TagsDisplay},
  computed: {
    ...mapState([
        'courses', 'coursesLoading', 'totalCourses',
    ]),
    ...mapGetters([
        'courses', 'coursesLoading', 'totalCourses', 'dataTableFooterProps',
    ]),
  },
  methods: {
    ...mapActions([
      'getCourses', 'getCourse', 'clearCourseApiError', 'setOpenDialog', 'setCoursePaginationOptions',
      'setCoursesSearch',
    ]),
    addItem() {
      this.clearCourseApiError();
      this.setOpenDialog('courseAdd');
    },
    async editItem(item) {
      this.clearCourseApiError();
      this.setOpenDialog('courseEdit');
      await this.getCourse(item);
      if (!this.currentCourseItem) {
        this.getCourses();
      }
    },
    async deleteItem(item) {
      this.clearCourseApiError();
      this.setOpenDialog('courseDelete');
      await this.getCourse(item);
      if (!this.currentCourseItem) {
        this.getCourses();
      }
    },
    refresh() {
      this.getCourses();
    },
    clear() {
      this.setCoursesSearch('');
    },
  },
  mounted () {
    this.setCoursesSearch('');
    this.getCourses();
  },
  data() {
    return {
      search: '',
      debouncedSearch: '',
      options: {
        itemsPerPage: 10,
      },
      headers: [
        {
          text: 'Course Name',
          align: 'start',
          value: 'name',
          width: '10%',
        },
        {
          text: 'Price',
          align: 'start',
          value: 'priceInUnits',
          width: '10%',
        },
        {
          text: 'Popular',
          align: 'start',
          value: 'popular',
          width: '15px',
        },
        {
          text: 'Level',
          align: 'start',
          value: 'level',
          width: '10%',
        },
        {
          text: 'Awarding Body',
          align: 'start',
          value: 'certificationBody',
          width: '10%',
        },
        {
          text: 'Description',
          align: 'start',
          value: 'description',
          width: '60%',
        },
        {
          text: 'Categories',
          align: 'start',
          value: 'categories',
          width: '10%',
        },
        {
          text: 'Tags',
          align: 'start',
          value: 'tags',
          width: '10%',
        },
        {
          text: 'Resources',
          value: 'resources',
          sortable: false,
          width: '10px',
        },
        {
          text: 'Actions',
          value: 'actions',
          sortable: false,
          width: '20px',
        },
      ],
    };
  },
  watch: {
    options: {
      handler() {
        this.setCoursePaginationOptions(this.options);
        this.getCourses();
      },
      deep: true,
    },
    search: debounce(function(newVal) {
      this.debouncedSearch = newVal;
    }, 500),
    debouncedSearch: {
      handler() {
        this.setCoursesSearch(this.search ?? '');
        this.getCourses();
      },
    },
  },
};
</script>